// frontend/src/modules/esign/pages/DocumentsListPage.tsx
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, PenTool, ChevronLeft, ChevronRight, FileStack } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { DataTable, type DataTableColumn } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { Button } from '../../../components/ui/button';
import { statusPill, type StatusTone } from '../../../design-system/visual';
import { formatDateTime } from '../../../design-system/format';
import { usePermission } from '../../../core/auth/usePermission';
import { esignApi } from '../api';
import { UseTemplateDialog } from '../components/UseTemplateDialog';
import type { EsignDocumentListRow, EsignDocumentStatus } from '../types';

const PAGE_SIZE = 50;

const STATUS_TONE: Record<EsignDocumentStatus, StatusTone> = {
  draft: 'draft',
  sent: 'info',
  completed: 'success',
  declined: 'destructive',
  voided: 'muted',
};

const STATUS_LABEL: Record<EsignDocumentStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  completed: 'Completed',
  declined: 'Declined',
  voided: 'Voided',
};

export function DocumentsListPage() {
  const navigate = useNavigate();
  const canView = usePermission('esign.view');
  const canSend = usePermission('esign.send');

  const [rows, setRows] = React.useState<EsignDocumentListRow[]>([]);
  const [offset, setOffset] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [useDialogOpen, setUseDialogOpen] = React.useState(false);

  const load = React.useCallback((nextOffset: number) => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    esignApi.listDocuments({ limit: PAGE_SIZE, offset: nextOffset })
      .then((page) => {
        setRows(page.data);
        setTotal(page.meta.total);
        setHasMore(page.meta.has_more);
      })
      .catch((err: any) => setError(err?.message ?? 'Failed to load agreements'))
      .finally(() => setLoading(false));
  }, [canView]);

  React.useEffect(() => { load(offset); }, [load, offset]);

  if (!canView) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="E-Sign" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <NoAccessState description="Your role doesn't include e-sign. Ask a workspace owner if you think you should have access." />
        </div>
      </div>
    );
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const newAgreementButton = canSend ? (
    <Link to="/dashboard/esign/new">
      <Button size="default" className="gap-1.5">
        <Plus className="h-3.5 w-3.5" />
        New agreement
      </Button>
    </Link>
  ) : null;

  const headerActions = (
    <div className="flex items-center gap-2">
      <Link to="/dashboard/esign/templates">
        <Button variant="outline" size="default">Templates</Button>
      </Link>
      {canSend && (
        <Button variant="outline" size="default" className="gap-1.5" onClick={() => setUseDialogOpen(true)}>
          <FileStack className="h-3.5 w-3.5" />
          New from template
        </Button>
      )}
      {newAgreementButton}
    </div>
  );

  const columns: DataTableColumn<EsignDocumentListRow>[] = [
    { key: 'title', header: 'Title', cell: (r) => <span className="font-medium">{r.title}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <span className={statusPill(STATUS_TONE[r.status])}>{STATUS_LABEL[r.status]}</span>,
    },
    {
      key: 'signers',
      header: 'Signers',
      cell: (r) => `${r.signed_count} / ${r.recipient_count}`,
    },
    {
      key: 'created_at',
      header: 'Created',
      cell: (r) => formatDateTime(r.created_at),
    },
  ];

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Money · E-Sign"
        title="E-Sign"
        description="Send documents for legally binding electronic signature."
        actions={headerActions}
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {error ? (
          <ErrorState title="Couldn't load agreements" description={error} onRetry={() => load(offset)} />
        ) : (
          <DataTable<EsignDocumentListRow>
            loading={loading}
            rows={rows}
            rowKey={(r) => r.id}
            // Drafts resume in the wizard — the detail page has nothing useful
            // to show for a document that hasn't been prepared yet.
            onRowClick={(r) => navigate(
              r.status === 'draft' ? `/dashboard/esign/${r.id}/prepare` : `/dashboard/esign/${r.id}`,
            )}
            columns={columns}
            emptyState={
              <EmptyState
                icon={<PenTool />}
                title="No agreements yet"
                description="Send your first document for signature — upload a PDF, add recipients, place signature fields, and send."
                action={newAgreementButton ?? undefined}
              />
            }
          />
        )}

        {!error && total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-[13px] text-ink/65 dark:text-white/65">
            <span className="tabular-nums">
              Showing {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                disabled={offset === 0 || loading}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </Button>
              <span className="tabular-nums px-1">Page {currentPage} of {pageCount}</span>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                disabled={!hasMore || loading}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <UseTemplateDialog open={useDialogOpen} onOpenChange={setUseDialogOpen} />
    </div>
  );
}
